// Dark navy sidebar in Drata's layout and order, built from the schema nav and module nav entries.
import { Link, useRouterState } from '@tanstack/react-router'
import { useMemo } from 'react'
import { ClearpolLogo } from '../components/Brand'
import { useAuth } from '../lib/auth-context'
import type { NavEntry } from '../lib/modules'
import { buildNav, isNavActive } from '../lib/nav'
import { useSchema } from '../lib/queries'
import { cn } from '../lib/utils'
import { webModules } from '../modules'
import { DEFAULT_NAV_ICON, NAV_ICONS } from './nav-icons'

function NavIcon({ icon }: { icon: NavEntry['icon'] }) {
  if (typeof icon === 'function') {
    const C = icon
    return <C className="size-[18px] shrink-0" />
  }
  const C = (icon && NAV_ICONS[icon]) || DEFAULT_NAV_ICON
  return <C className="size-[18px] shrink-0" aria-hidden />
}

export function useNavSections() {
  const { viewer } = useAuth()
  const { data: schema } = useSchema()
  return useMemo(() => buildNav({ schema: schema ?? null, viewer, extra: webModules.nav }), [schema, viewer])
}

export function Sidebar({ onNavigate, className }: { onNavigate?: () => void; className?: string }) {
  const sections = useNavSections()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const top = sections.filter((s) => s.group !== 'Settings')
  const bottom = sections.find((s) => s.group === 'Settings')

  const item = (n: NavEntry) => {
    const active = isNavActive(n, pathname)
    return (
      <li key={n.key}>
        <Link
          to={n.to}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'group relative flex items-center gap-3 rounded-md px-3 py-2 text-[13.5px] font-medium text-navy-200 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-teal-400',
            active && 'bg-white/10 text-white',
          )}
        >
          {active ? (
            <span aria-hidden className="absolute top-1.5 bottom-1.5 left-0 w-[3px] rounded-r bg-teal-500" />
          ) : null}
          <NavIcon icon={n.icon} />
          <span className="truncate">{n.label}</span>
        </Link>
      </li>
    )
  }

  return (
    <nav aria-label="Main" className={cn('flex h-full w-60 flex-col bg-navy-900 text-white', className)}>
      <div className="flex h-14 shrink-0 items-center px-5">
        <Link
          to="/"
          onClick={onNavigate}
          aria-label="Clearpol Compliance home"
          className="rounded focus-visible:outline-2 focus-visible:outline-teal-400"
        >
          <ClearpolLogo inverted className="h-6" />
        </Link>
        <span className="mt-1 ml-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-400">
          Compliance
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {top.map((s) => (
          <div key={s.group} className="mt-4 first:mt-2">
            {s.label ? (
              <p className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-300">
                {s.label}
              </p>
            ) : null}
            <ul className="space-y-0.5">{s.items.map(item)}</ul>
          </div>
        ))}
      </div>
      {bottom ? (
        <div className="shrink-0 border-t border-white/10 px-3 py-3">
          <ul className="space-y-0.5">{bottom.items.map(item)}</ul>
        </div>
      ) : null}
    </nav>
  )
}
